import React, {useEffect, useState} from 'react';
import {
  Typography,
  Grid,
  List,
  makeStyles,
  Collapse,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Container,
} from '@material-ui/core';
import {ExpandLess, ExpandMore, MenuBook, Link, LinkOff, Refresh} from '@material-ui/icons';
import {Alert} from '@material-ui/lab';
import {options} from '../components/subcomponents/adstxt/options';
import FadeIn from 'react-fade-in/lib/FadeIn';
import {useParams} from 'react-router-dom';
import {APICall} from '../api/api';

const GridIContainer = props => <Grid container {...props} />;

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  wrapper: {
    flexGrow: 1,
    borderRadius: '4px',
    border: '1px solid #7979793b',
    marginTop: 20,
    background: theme.palette.background.paper,
  },
  collapseContainer: {
    width: '100%',
  },
  formControl: {
    margin: theme.spacing(1),
    width: '100%',
    paddingBottom: theme.spacing(1),
  },
  redirectAlert: {
    marginBottom: 10,
  },
  codeOption: {
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    fontSize: '0.8rem',
    padding: 20,
    background: '#00000038',
    borderRadius: 4,
    marginTop: 20,
    marginBottom: 20,
  },
  header: {
    marginBottom: theme.spacing(2),
  },

  statusContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statusButton: {
    marginLeft: theme.spacing(1),
  },
  domainFavicon: {
    height: 16,
    width: 16,
    position: 'relative',
    backgroundColor: 'white',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '80%',
    borderRadius: 3,
    display: 'inline-block',
    marginRight: 6,
    verticalAlign: 'middle',
    //  top: -2,
  },
}));

// Hook to parse path parameters
const useUrlParams = () => {
  const params = useParams();

  return {
    isSelfServe: params.mode === 'selfserve',
    accountId: params.accountId,
    domainId: params.domainId,
    domainName: decodeURIComponent(params.domainName || ''),
  };
};

// Helper function to generate redirect status
const getRedirectStatus = (accountId, isSelfServe, currentAdsTXTurl, domainName, domainId) => {
  const baseRedirectUrl = `https://wb.setupad.com/api/getAdsTXT`;
  const requiredRedirectURL = isSelfServe
    ? `${baseRedirectUrl}/${accountId}/selfserve/${domainId}`
    : `${baseRedirectUrl}/${accountId}/file/${domainId}`;

  if (currentAdsTXTurl?.includes(requiredRedirectURL)) {
    return {
      success: true,
      message: 'Redirected to Setupad',
      alert: 'success',
      icon: <Link />,
      tooltip: `Redirect is set up correctly and ${domainName}/ads.txt redirects to ads.txt hosted by Setupad.`,
    };
  } else if (currentAdsTXTurl?.includes(baseRedirectUrl)) {
    return {
      success: false,
      message: requiredRedirectURL,
      alert: 'error',
      icon: <LinkOff />,
      tooltip: `${domainName}/ads.txt redirects to ads.txt hosted by Setupad, but the redirect URL is incorrect. Please check the instructions below to find the correct URL.`,
    };
  } else {
    return {
      success: false,
      message: 'Redirect not detected',
      alert: 'warning',
      icon: <Link />,
      tooltip: 'Please follow the instructions below to set up ads.txt redirect correctly.',
    };
  }
};

export const PublicAdsTxtRedirect = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [adsTxtStatus, setAdsTxtStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusError, setStatusError] = useState(null);

  // Get URL parameters
  const {isSelfServe, accountId, domainId, domainName} = useUrlParams();

  // Create a domain object from URL params if provided
  const domains =
    domainId && domainName
      ? [
          {
            id: domainId,
            title: domainName,
          },
        ]
      : [];

  // Function to check ads.txt redirect status
  const checkAdsTxtStatus = async () => {
    if (!accountId || !domainId || !domainName) return;

    setCheckingStatus(true);
    setStatusError(null);

    try {
      const adsTxtData = await APICall({
        endpoint: 'checkAdsTxt',
        params: `${accountId}/${domainName}/${domainId}`,
        options: {
          method: 'get',
          headers: {
            'Cache-Control': 'max-age=0, no-cache',
            'Content-Type': 'application/json',
          },
        },
      });

      // Generate redirect status
      const redirectStatus = getRedirectStatus(
        accountId,
        isSelfServe,
        adsTxtData.currentAdsTXT?.url,
        domainName,
        domainId
      );

      setAdsTxtStatus({
        ...adsTxtData,
        redirectStatus,
      });
    } catch (error) {
      setStatusError(error.message || 'Failed to check ads.txt status');
    } finally {
      setCheckingStatus(false);
    }
  };

  useEffect(() => {
    // Auto-select domain if provided in URL params
    if (domainId) {
      setSelectedDomain(domainId);
      // Check ads.txt status when component mounts
      checkAdsTxtStatus();
    }
  }, [domainId, accountId, domainName]);

  // Validate required parameters
  if (!accountId) {
    return (
      <Container className={classes.container}>
        <Alert severity="error">
          Missing required parameter: accountId. Please provide valid URL parameters.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className={classes.container}>
      <FadeIn>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className={classes.header}>
              <Typography variant={'h3'} gutterBottom>
                Setupad Ads.txt instructions
              </Typography>

              <Typography gutterBottom>
                {isSelfServe && (
                  <>
                    See our platform-specific setup guides to implement the ads.txt redirect for{' '}
                    <div
                      className={classes.domainFavicon}
                      style={{
                        backgroundImage: `url(https://www.google.com/s2/favicons?domain=https://${domainName}&sz=256)`,
                      }}
                    />
                    <strong>{domainName}</strong> effortlessly.
                  </>
                )}
                {!isSelfServe && (
                  <>
                    We add new partners regularly, so setting up an automated redirect to your
                    ads.txt hosted by Setupad is the easiest way to keep your file updated. Once the
                    redirect is set up, you can add custom new lines, which will be included in the
                    redirected ads.txt file. See our platform-specific setup guides to implement the
                    redirect effortlessly.
                  </>
                )}
              </Typography>
            </div>

            {domainName && (
              <div className={classes.statusContainer}>
                {checkingStatus && (
                  <Alert
                    severity="info"
                    action={
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        disabled
                        startIcon={<Refresh />}
                      >
                        Checking...
                      </Button>
                    }
                  >
                    <span className={classes.domainWithIcon}>
                      Checking ads.txt redirect status for{' '}
                      <div
                        className={classes.domainFavicon}
                        style={{
                          backgroundImage: `url(https://www.google.com/s2/favicons?domain=https://${domainName}&sz=256)`,
                        }}
                      />
                      <strong>{domainName}</strong>...
                    </span>
                  </Alert>
                )}

                {statusError && (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={checkAdsTxtStatus}
                        startIcon={<Refresh />}
                        className={classes.statusButton}
                      >
                        Check again
                      </Button>
                    }
                  >
                    Error: {statusError}
                  </Alert>
                )}

                {adsTxtStatus?.redirectStatus && !checkingStatus && !statusError && (
                  <Alert
                    severity={adsTxtStatus.redirectStatus.alert}
                    action={
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={checkAdsTxtStatus}
                        startIcon={<Refresh />}
                        className={classes.statusButton}
                      >
                        Check again
                      </Button>
                    }
                    icon={adsTxtStatus.redirectStatus.icon}
                  >
                    <Typography style={{fontWeight: 600}}>
                      {adsTxtStatus.redirectStatus.message}
                    </Typography>
                    <Typography variant="body2" style={{marginTop: 4}}>
                      {adsTxtStatus.redirectStatus.tooltip}
                    </Typography>
                  </Alert>
                )}
              </div>
            )}
          </Grid>
        </Grid>

        <Grid container className={classes.wrapper} spacing={1} direction={'column'}>
          <List disablePadding component={GridIContainer}>
            {options.map((option, index) => {
              return (
                <React.Fragment key={index}>
                  <ListItem
                    disableRipple
                    button
                    selected={open === index}
                    style={{minHeight: 63, borderTop: '1px solid #7979793b'}}
                    onClick={() => {
                      setOpen(open === index ? null : index);
                    }}
                  >
                    <ListItemIcon>
                      <MenuBook />
                    </ListItemIcon>
                    <ListItemText primary={option.title} />
                    {open === index ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse
                    in={open === index}
                    timeout={0}
                    classes={{
                      container: classes.collapseContainer,
                    }}
                  >
                    <div style={{padding: 20}}>
                      {domains && domains.length > 0 && (
                        <Grid item xs={12}>
                          <FormControl className={classes.formControl}>
                            <InputLabel shrink={true} id="domain-label">
                              <Typography
                                gutterBottom
                                color={selectedDomain ? 'inherit' : 'primary'}
                              >
                                {selectedDomain
                                  ? 'Selected domain'
                                  : 'Select domain to see instructions'}
                              </Typography>
                            </InputLabel>
                            <Select
                              color={'primary'}
                              fullWidth
                              labelId="domain-label"
                              value={selectedDomain || ''}
                              open={open === index && menuOpen === index}
                              onChange={event => {
                                setSelectedDomain(event.target.value);
                                setMenuOpen(null);
                              }}
                              onOpen={() => setMenuOpen(index)}
                              onClose={() => setMenuOpen(null)}
                              MenuProps={{
                                getContentAnchorEl: null,
                              }}
                            >
                              {domains.map(domain => (
                                <MenuItem key={domain.id} value={domain.id}>
                                  {domain.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      <div style={{opacity: selectedDomain || !domains.length ? 1 : 0.1}}>
                        {option.content.map((item, itemIndex) => {
                          if (item.type === 'text') {
                            return (
                              <Typography key={itemIndex} gutterBottom>
                                {item.text}
                              </Typography>
                            );
                          }
                          if (item.type === 'code') {
                            // Code replacements
                            let text = item.text
                              .replaceAll('{accountId}', accountId)
                              .replaceAll('{domainId}', selectedDomain || domainId || '{domainId}')
                              .replaceAll('&nbsp;', ' ');

                            // Redirect url replacements by mode
                            if (isSelfServe) {
                              text = text.replaceAll('{mode}', 'selfserve');
                            } else {
                              text = text.replaceAll('{mode}', 'file');
                            }

                            return (
                              <Typography
                                key={itemIndex}
                                className={classes.codeOption}
                                gutterBottom
                                style={{opacity: selectedDomain || !domains.length ? 1 : 0}}
                              >
                                {text}
                              </Typography>
                            );
                          }
                          if (item.type === 'step') {
                            return (
                              <Typography key={itemIndex} gutterBottom>
                                • {item.text}
                              </Typography>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </Grid>
      </FadeIn>
    </Container>
  );
};

export default PublicAdsTxtRedirect;
